import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDownRight, MessageCircle } from "lucide-react";
import { MaskLines } from "./Reveal";
import { useLang, waLink } from "@/lib/i18n";
import { scrollToId } from "@/lib/smooth";

const EASE = [0.16, 1, 0.3, 1];

export default function Hero() {
  const { t, lang } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.22]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 18 });
  const sy = useSpring(my, { stiffness: 60, damping: 18 });
  const rotY = useTransform(sx, [-0.5, 0.5], [4, -4]);
  const rotX = useTransform(sy, [-0.5, 0.5], [-3, 3]);
  const spotX = useTransform(sx, [-0.5, 0.5], ["30%", "70%"]);
  const spotY = useTransform(sy, [-0.5, 0.5], ["30%", "70%"]);
  const spot = useTransform([spotX, spotY], ([x, y]) => `radial-gradient(520px circle at ${x} ${y}, rgba(255,255,255,0.10), transparent 60%)`);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section id="top" ref={ref} onMouseMove={onMove} data-testid="hero-section" className="grain relative min-h-[100svh] overflow-hidden [perspective:1400px]">
      <motion.div style={{ y: imgY, scale: imgScale, rotateX: rotX, rotateY: rotY }} className="absolute inset-0 origin-center">
        <motion.img
          src="/img/hero.jpg"
          alt="Transcars fleet: Iveco Daily car carrier and Mercedes Actros L with driver"
          className="h-full w-full object-cover object-[50%_40%]"
          initial={{ clipPath: "inset(18% 12% 18% 12% round 28px)", filter: "brightness(0.4)" }}
          animate={{ clipPath: "inset(0% 0% 0% 0% round 0px)", filter: "brightness(0.62)" }}
          transition={{ duration: 1.6, ease: EASE, delay: 0.1 }}
        />
      </motion.div>
      <motion.div style={{ background: spot }} className="pointer-events-none absolute inset-0" />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#090A0C] via-[#090A0C]/55 to-[#090A0C]/30" />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-[#090A0C]/85 via-[#090A0C]/30 to-transparent" />

      <motion.div style={{ y: textY }} className="relative z-10 mx-auto flex min-h-[100svh] max-w-[1400px] flex-col justify-end px-5 pb-16 pt-28 sm:px-8 sm:pb-20">
        <motion.p initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.5, duration: 0.8 }} className="mb-6 flex items-center gap-3 font-mono-tc text-xs font-bold uppercase tracking-[0.25em] text-white">
          <span className="h-px w-10 bg-white" /> {t.hero.eyebrow}
        </motion.p>
        <h1 data-testid="hero-title" className="font-display text-[15vw] font-black uppercase italic leading-[0.86] tracking-tight sm:text-[11vw] lg:text-[9.2rem]">
          <MaskLines key={lang} lines={t.hero.lines} delay={0.55} lineClass="[&:nth-child(1)]:text-white" />
        </h1>
        <div className="mt-10 grid gap-8 lg:grid-cols-[1fr_auto] lg:items-end">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.9, ease: EASE }} className="max-w-xl text-base leading-relaxed text-zinc-300 md:text-lg">
            {t.hero.sub}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.25, duration: 0.9, ease: EASE }} className="flex flex-wrap gap-3">
            <button data-testid="hero-quote-button" onClick={() => scrollToId("quote")} className="group flex items-center gap-3 rounded-full bg-white py-4 pl-7 pr-5 font-semibold text-black transition-colors hover:bg-zinc-300">
              {t.hero.cta}
              <span className="grid h-8 w-8 place-items-center rounded-full bg-black text-white transition-transform duration-500 group-hover:rotate-[-45deg]">
                <ArrowDownRight className="h-4 w-4" />
              </span>
            </button>
            <a data-testid="hero-whatsapp-button" href={waLink(t.waDefault)} target="_blank" rel="noreferrer" className="flex items-center gap-2 rounded-full border border-white/25 px-6 py-4 font-semibold backdrop-blur-md transition-colors hover:border-white hover:bg-white hover:text-black">
              <MessageCircle className="h-5 w-5" /> {t.hero.wa}
            </a>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
}
